import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase.js'
import { apiFetch } from '../../lib/api.js'
import { useGameStore } from '../../stores/gameStore.js'
import { startImpersonation, clearImpersonation } from '../../lib/impersonation.js'

const PROFILE_TYPE_LABELS = {
  'enseignant-formateur': 'Enseignant / Formateur',
  'eleve-apprenant': 'Élève / Apprenant',
  'parent': 'Parent',
  'autre': 'Autre',
}

function fmtDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })
}

const SORT_COLS = [
  { key: 'nom',             label: 'NOM' },
  { key: 'prenom',          label: 'Prénom' },
  { key: 'email',           label: 'Email' },
  { key: 'role',            label: 'Rôle' },
  { key: 'profile_type',    label: 'Profil' },
  { key: 'code_postal',     label: 'CP' },
  { key: 'created_at',      label: 'Inscription' },
  { key: 'last_sign_in_at', label: 'Dernière connexion' },
]

function SortArrow({ active, dir }) {
  if (!active) return <span className="ml-1 text-white/20">↕</span>
  return <span className="ml-1 text-white/70">{dir === 'asc' ? '↑' : '↓'}</span>
}

export default function UserManager() {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentUserId, setCurrentUserId] = useState(null)
  const [updating, setUpdating] = useState(null)

  const [search, setSearch] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [filterProfile, setFilterProfile] = useState('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')

  const [openMenu, setOpenMenu] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [impersonating, setImpersonating] = useState(null)
  const reset = useGameStore(s => s.reset)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentUserId(session?.user?.id ?? null)
    })
    fetchUsers()
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    if (openMenu === null) return
    function handleClose() { setOpenMenu(null) }
    document.addEventListener('click', handleClose)
    return () => document.removeEventListener('click', handleClose)
  }, [openMenu])

  async function fetchUsers() {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch('/api/admin/users')
      setUsers(data)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function updateRole(userId, newRole) {
    setUpdating(userId)
    try {
      await apiFetch(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole }),
      })
      await fetchUsers()
    } catch (e) {
      setError(e.message)
    } finally {
      setUpdating(null)
      setOpenMenu(null)
    }
  }

  async function handleImpersonate(u) {
    setImpersonating(u.id)
    setOpenMenu(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const adminSession = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      }
      const data = await apiFetch(`/api/admin/users/${u.id}/impersonate`, { method: 'POST' })
      startImpersonation({ targetEmail: u.email, adminSession })
      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: data.hashed_token,
        type: 'email',
      })
      if (otpError) {
        clearImpersonation()
        throw otpError
      }
      reset()
      navigate('/home')
    } catch (e) {
      setError(e.message)
    } finally {
      setImpersonating(null)
    }
  }

  async function deleteUser(userId) {
    setDeleting(true)
    try {
      await apiFetch(`/api/admin/users/${userId}`, { method: 'DELETE' })
      await fetchUsers()
      setDeleteConfirm(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setDeleting(false)
    }
  }

  function handleSort(col) {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(col)
      setSortDir('asc')
    }
  }

  function exportCSV() {
    const headers = [
      'Email', 'Nom', 'Prénom', 'Rôle', 'Profil',
      'Établissement', 'Code postal', 'Niveaux',
      'Onboarding complété', 'Inscription', 'Dernière connexion',
    ]
    const rows = users.map(u => [
      u.email ?? '',
      u.nom ?? '',
      u.prenom ?? '',
      u.role ?? '',
      u.profile_type ? (PROFILE_TYPE_LABELS[u.profile_type] ?? u.profile_type) : '',
      u.etablissement ?? '',
      u.code_postal ?? '',
      (u.niveaux ?? []).join(', '),
      u.onboarding_completed ? 'Oui' : 'Non',
      fmtDateTime(u.created_at),
      fmtDateTime(u.last_sign_in_at),
    ])
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `kartquiz-utilisateurs-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const adminCount = users.filter(u => u.role === 'admin').length

  const filtered = users.filter(u => {
    if (filterRole && u.role !== filterRole) return false
    if (filterProfile && u.profile_type !== filterProfile) return false
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      const hit =
        (u.email ?? '').toLowerCase().includes(q) ||
        (u.nom ?? '').toLowerCase().includes(q) ||
        (u.prenom ?? '').toLowerCase().includes(q)
      if (!hit) return false
    }
    return true
  })

  const sorted = [...filtered].sort((a, b) => {
    let va, vb
    switch (sortBy) {
      case 'nom':             va = (a.nom ?? '').toLowerCase();          vb = (b.nom ?? '').toLowerCase();          break
      case 'prenom':          va = (a.prenom ?? '').toLowerCase();       vb = (b.prenom ?? '').toLowerCase();       break
      case 'email':           va = (a.email ?? '').toLowerCase();        vb = (b.email ?? '').toLowerCase();        break
      case 'role':            va = a.role ?? '';                          vb = b.role ?? '';                          break
      case 'profile_type':    va = a.profile_type ?? '';                  vb = b.profile_type ?? '';                  break
      case 'code_postal':     va = a.code_postal ?? '';                   vb = b.code_postal ?? '';                   break
      case 'last_sign_in_at': va = a.last_sign_in_at ?? '';              vb = b.last_sign_in_at ?? '';              break
      default:                va = a.created_at ?? '';                    vb = b.created_at ?? ''
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1
    if (va > vb) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  // ── Actions dropdown ────────────────────────────────────────────
  function ActionsMenu({ u }) {
    const isOpen = openMenu === u.id
    const isCurrentUser = u.id === currentUserId
    const isLastAdmin = u.role === 'admin' && adminCount <= 1
    const isImpersonating = impersonating === u.id

    function toggleMenu(e) {
      e.stopPropagation()
      setOpenMenu(isOpen ? null : u.id)
    }

    return (
      <div className="relative">
        <button
          onClick={toggleMenu}
          className="w-8 h-8 flex flex-col items-center justify-center gap-[3px] rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition"
          title="Actions"
          aria-label="Actions"
        >
          <span className="w-1 h-1 rounded-full bg-current" />
          <span className="w-1 h-1 rounded-full bg-current" />
          <span className="w-1 h-1 rounded-full bg-current" />
        </button>

        {isOpen && (
          <div
            onClick={e => e.stopPropagation()}
            className="absolute right-0 top-9 z-50 w-52 bg-gray-800 border border-white/10 rounded-xl shadow-2xl overflow-hidden"
          >
            <button
              onClick={() => { navigate(`/admin/users/${u.id}/profile`); setOpenMenu(null) }}
              className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:bg-white/10 hover:text-white transition flex items-center gap-2"
            >
              <span>✏️</span> Modifier
            </button>

            {!isCurrentUser && !isLastAdmin && u.role === 'admin' && (
              <button
                onClick={() => updateRole(u.id, 'host')}
                disabled={updating === u.id}
                className="w-full text-left px-4 py-2.5 text-sm text-white/70 hover:bg-white/10 hover:text-white transition disabled:opacity-50 flex items-center gap-2"
              >
                <span>⬇️</span> {updating === u.id ? '…' : 'Rétrograder hôte'}
              </button>
            )}

            {!isCurrentUser && u.role === 'host' && (
              <button
                onClick={() => updateRole(u.id, 'admin')}
                disabled={updating === u.id}
                className="w-full text-left px-4 py-2.5 text-sm text-purple-300 hover:bg-purple-500/20 transition disabled:opacity-50 flex items-center gap-2"
              >
                <span>⬆️</span> {updating === u.id ? '…' : 'Promouvoir admin'}
              </button>
            )}

            {!isCurrentUser && u.role !== 'admin' && (
              <button
                onClick={() => handleImpersonate(u)}
                disabled={isImpersonating}
                className="w-full text-left px-4 py-2.5 text-sm text-orange-300 hover:bg-orange-500/10 transition disabled:opacity-50 flex items-center gap-2"
              >
                <span>👁</span> {isImpersonating ? '…' : 'Se connecter en tant que'}
              </button>
            )}

            {!isCurrentUser && (
              <>
                <div className="border-t border-white/10 my-1" />
                <button
                  onClick={() => { setDeleteConfirm(u); setOpenMenu(null) }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition flex items-center gap-2"
                >
                  <span>🗑</span> Supprimer le compte
                </button>
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-white/40">Chargement…</div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-white">Utilisateurs</h1>
          <span className="text-sm text-white/40">
            {sorted.length !== users.length ? `${sorted.length} / ${users.length}` : `${users.length} compte${users.length > 1 ? 's' : ''}`}
          </span>
        </div>
        <button
          onClick={exportCSV}
          className="text-sm px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition flex items-center gap-1.5 shrink-0"
        >
          ↓ Exporter CSV
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Recherche + filtres */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher par nom, prénom, email…"
          className="flex-1 min-w-[200px] border border-white/20 bg-white/10 text-white placeholder-white/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <select
          value={filterRole}
          onChange={e => setFilterRole(e.target.value)}
          className="border border-white/20 bg-gray-800 text-white/80 rounded-lg px-3 py-2 text-sm focus:outline-none"
        >
          <option value="">Tous les rôles</option>
          <option value="admin">🛡 Admin</option>
          <option value="host">🧑‍🏫 Hôte</option>
        </select>
        <select
          value={filterProfile}
          onChange={e => setFilterProfile(e.target.value)}
          className="border border-white/20 bg-gray-800 text-white/80 rounded-lg px-3 py-2 text-sm focus:outline-none"
        >
          <option value="">Tous les profils</option>
          <option value="enseignant-formateur">Enseignant / Formateur</option>
          <option value="eleve-apprenant">Élève / Apprenant</option>
          <option value="parent">Parent</option>
          <option value="autre">Autre</option>
        </select>
      </div>

      {/* ── Table desktop (md+) ─────────────────────────────────── */}
      <div className="hidden md:block bg-gray-800 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 border-b border-white/10">
            <tr>
              {SORT_COLS.map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className="text-left px-3 py-3 text-white/60 font-medium whitespace-nowrap cursor-pointer hover:text-white/80 select-none"
                >
                  {col.label}
                  <SortArrow active={sortBy === col.key} dir={sortDir} />
                </th>
              ))}
              <th className="px-3 py-3 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {sorted.map(u => (
              <tr key={u.id} className="hover:bg-white/5 transition group">
                <td className="px-3 py-3 text-white font-medium whitespace-nowrap">
                  {u.nom || <span className="text-white/25">—</span>}
                  {u.id === currentUserId && (
                    <span className="ml-1 text-xs text-white/30">(vous)</span>
                  )}
                  {u.tags?.includes('early_adopter') && (
                    <span className="ml-1.5 text-xs bg-amber-400/15 text-amber-300 font-semibold px-1.5 py-0.5 rounded-full">⭐ EA</span>
                  )}
                </td>
                <td className="px-3 py-3 text-white/80 whitespace-nowrap">
                  {u.prenom || <span className="text-white/25">—</span>}
                </td>
                <td className="px-3 py-3 text-white/60 max-w-[180px] truncate">{u.email}</td>
                <td className="px-3 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    u.role === 'admin'
                      ? 'bg-purple-500/20 text-purple-300'
                      : 'bg-blue-500/20 text-blue-300'
                  }`}>
                    {u.role === 'admin' ? '🛡 Admin' : '🧑‍🏫 Hôte'}
                  </span>
                </td>
                <td className="px-3 py-3 text-white/50 text-xs whitespace-nowrap">
                  {u.profile_type ? PROFILE_TYPE_LABELS[u.profile_type] : <span className="text-white/20">—</span>}
                </td>
                <td className="px-3 py-3 text-white/50 text-xs">
                  {u.code_postal || <span className="text-white/20">—</span>}
                </td>
                <td className="px-3 py-3 text-white/40 text-xs whitespace-nowrap">{fmtDateTime(u.created_at)}</td>
                <td className="px-3 py-3 text-white/40 text-xs whitespace-nowrap">{fmtDateTime(u.last_sign_in_at)}</td>
                <td className="px-3 py-3 text-right">
                  <ActionsMenu u={u} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Cards mobile (< md) ─────────────────────────────────── */}
      <div className="md:hidden space-y-3">
        {sorted.map(u => (
          <div key={u.id} className="bg-gray-800 border border-white/10 rounded-xl p-4">
            {/* Ligne principale */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">
                  {[u.prenom, u.nom].filter(Boolean).join(' ') || (
                    <span className="font-normal text-white/40">Sans nom</span>
                  )}
                  {u.id === currentUserId && (
                    <span className="ml-1 text-xs text-white/30">(vous)</span>
                  )}
                </p>
                <p className="text-xs text-white/50 truncate mt-0.5">{u.email}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {u.tags?.includes('early_adopter') && (
                  <span className="text-xs bg-amber-400/15 text-amber-300 font-semibold px-1.5 py-0.5 rounded-full">⭐ EA</span>
                )}
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  u.role === 'admin'
                    ? 'bg-purple-500/20 text-purple-300'
                    : 'bg-blue-500/20 text-blue-300'
                }`}>
                  {u.role === 'admin' ? '🛡 Admin' : '🧑‍🏫 Hôte'}
                </span>
                <ActionsMenu u={u} />
              </div>
            </div>

            {/* Détails */}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-white/40">
              {u.profile_type && (
                <span>{PROFILE_TYPE_LABELS[u.profile_type]}</span>
              )}
              {u.code_postal && <span>📍 {u.code_postal}</span>}
              <span>Inscrit le {fmtDate(u.created_at)}</span>
              {u.last_sign_in_at && (
                <span>Connexion {fmtDate(u.last_sign_in_at)}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {sorted.length === 0 && !loading && (
        <div className="text-center py-12 text-white/30 text-sm">
          Aucun utilisateur ne correspond aux filtres.
        </div>
      )}

      {/* ── Modale de confirmation de suppression ───────────────── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-gray-800 border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h2 className="text-lg font-semibold text-white mb-2">Supprimer le compte ?</h2>
            <p className="text-sm text-white/60 mb-1">
              Cette action est <strong className="text-white/80">irréversible</strong>. L'authentification, le profil et toutes les données associées à{' '}
              <strong className="text-white">{deleteConfirm.email}</strong> seront définitivement supprimés.
            </p>
            <p className="text-xs text-white/35 mb-5">
              Les données de parties existantes (historique) peuvent subsister.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white/70 text-sm transition disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={() => deleteUser(deleteConfirm.id)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition disabled:opacity-60"
              >
                {deleting ? '…' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
